import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { authToken } from '../../scripts/authentication.script';

export class ExpenseReportDA {
  static apiResponse: ApiResponse

  public static async getExpenseReports(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    status: string,
    payment: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    try {
      const params = new URLSearchParams()
      if (interval) params.append('interval', interval)
      if (start_date) params.append('start_date', start_date)
      if (end_date) params.append('end_date', end_date)
      if (contact_id) params.append('id_contact', contact_id)
      if (status) params.append('status', status)
      if (payment) params.append('payment', payment)
      if (sort_field) params.append('sort_by', sort_field)
      if (sort_mode) params.append('sort_order', sort_mode)
      if (group_by) params.append('group_by', group_by)
      if (search) params.append('search', search)
      if (all) {
        params.append('all', 'true')
      } else {
        params.append('page', String(page))
        params.append('limit', String(limit))
      }

      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports?${params.toString()}`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.get(url, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async getExpenseReport(
    id_company: string,
    id_expense_report: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.get(url, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async postExpenseReport(
    id_company: string,
    id_employee: string,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string, merchant?: string, expense_date?: string, voucher_name?: string, voucher_file?: any, voucher_view?: string }[] }[],
    company_detail: any
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports`
      const config = { headers: { Authorization: authToken() } }

      const data = new FormData()
      data.append('id_employee', id_employee)
      data.append('concerning', concerning)
      data.append('created_date', date)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')
      data.append('company', JSON.stringify(company_detail))

      requirements.forEach((item, reqIdx) => {
        const cleanedContents = item.contents.map((c, contentIdx) => {
          const { voucher_file, voucher_view, voucher_name, ...rest } = c as any
          return {
            ...rest,
            voucher: voucher_file instanceof File ? { original_name: voucher_name } : (voucher_file ? true : undefined)
          }
        })
        data.append('requirements', JSON.stringify({ ...item, contents: cleanedContents }))

        item.contents.forEach((c: any, contentIdx) => {
          if (c.voucher_file instanceof File) {
            data.append(`requirement_voucher_${reqIdx}_${contentIdx}`, c.voucher_file, c.voucher_name || c.voucher_file.name)
          }
        })
      })

      const response = await axios.post(url, data, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async addLine(
    id_company: string,
    id_expense_report: string,
    id_expense: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/lines`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, { id_expense }, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async removeLine(
    id_company: string,
    id_expense_report: string,
    id_expense: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/lines/${id_expense}`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async putExpenseReport(
    id_company: string,
    id_expense_report: string,
    id_employee: string,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string, merchant?: string, expense_date?: string, voucher_name?: string, voucher_file?: any, voucher_view?: string }[] }[]
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}`
      const config = { headers: { Authorization: authToken() } }

      const data = new FormData()
      data.append('id_employee', id_employee)
      data.append('concerning', concerning)
      data.append('created_date', date)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')

      requirements.forEach((item, reqIdx) => {
        const cleanedContents = item.contents.map((c, contentIdx) => {
          const { voucher_file, voucher_view, voucher_name, ...rest } = c as any
          return {
            ...rest,
            voucher: voucher_file instanceof File ? { original_name: voucher_name } : (voucher_file ? true : undefined)
          }
        })
        data.append('requirements', JSON.stringify({ ...item, contents: cleanedContents }))

        item.contents.forEach((c: any, contentIdx) => {
          if (c.voucher_file instanceof File) {
            data.append(`requirement_voucher_${reqIdx}_${contentIdx}`, c.voucher_file, c.voucher_name || c.voucher_file.name)
          }
        })
      })

      const response = await axios.put(url, data, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async putExpenseReportStatus(
    id_company: string,
    id_expense_report: string,
    status: string,
    amount: number | null,
    content_accounts: { id_content: string, id_account_credit: string, id_account_debit: string }[] | null,
    effective_accounts: { id_content: string, id_account_tax: string }[] | null
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/status`
      const config = { headers: { Authorization: authToken() } }

      const data: any = { status }
      if (amount !== null) data.amount = amount
      if (content_accounts) data.content_accounts = content_accounts
      if (effective_accounts) data.effective_accounts = effective_accounts

      const response = await axios.put(url, data, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async submitExpenseReport(
    id_company: string,
    id_expense_report: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/submit`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.put(url, {}, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async invalidateExpenseReport(
    id_company: string,
    id_expense_report: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/status`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.put(url, { content_accounts: null }, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async rejectExpenseReport(
    id_company: string,
    id_expense_report: string,
    reason: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/reject`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.put(url, { reject_reason: reason }, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async postExpenseReportPayment(
    id_company: string,
    id_expense_report: string,
    amount_pay: string,
    amount_deposit: string,
    date: string,
    method: string,
    treasury_account_id: string,
    accounting_entry_id: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/payments`
      const config = { headers: { Authorization: authToken() } }

      const data: any = {
        amount_pay: Number(amount_pay),
        amount_deposit: Number(amount_deposit),
        payment_date: date,
        method: method,
        treasury_account: treasury_account_id,
        accounting_entry: accounting_entry_id,
      }

      const response = await axios.post(url, data, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async deleteExpenseReportPayment(
    id_company: string,
    id_expense_report: string,
    id_payment: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/payments/${id_payment}`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async deleteExpenseReport(
    id_company: string,
    id_expense_report: string
  ): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)

      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    }
  }

  public static async uploadVoucher(id_company: string, id_expense_report: string, file: File, role?: string): Promise<any> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      const config = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteVoucher(id_company: string, id_expense_report: string, index: number): Promise<any> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/vouchers/${index}`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async patchAccounts(id_company: string, id_expense_report: string, content_accounts: {id_content: string, id_account_debit: string, id_account_credit: string}[], effective_accounts?: {id_content: string, id_account_tax: string}[] | null, company?: {subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string} | null): Promise<ApiResponse> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/expense-reports/${id_expense_report}/accounts`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.patch(url, { content_accounts, effective_accounts: effective_accounts ?? undefined, company: company ?? undefined }, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }
}
